import React from 'react';

// components
import {
  Button,
  CircleIcons,
  TYPOGRAPHY_VARIANT,
  Typography,
  Icons,
  COLOR_VARIANTS,
  color,
  CurrencySymbol,
} from '@tajawal/ct-web-design-system';
import Styled from './FareFamilyCard.styled';
import BaggageItem from '../BaggageItem/BaggageItem';
import FareRuleItem from '@/app-common/components/FareRuleItem/';
import RuleItem from '@/app-common/components/RuleItem/RuleItem';
import ClippingSVG from '../ClippingSVG';

// hooks
import { useTranslation } from 'react-i18next';
import { useAB } from '@tajawal/web-common/lib';
import { useComputeSplitQueryAttrs } from '@/hooks/useComputeSplitQueryAttrs';
import { useQuery } from '@/hooks/useQuery';

// types
import { FareFamilyItinerary, ItineraryModel } from '@tajawal/web-flights-sdk/lib/types/itinerary.types';
import { GenericCallback } from '@/types/common.types';
import { FlightDirection } from '@tajawal/web-flights-sdk/lib/types/common.types';
import type FareFamiliesStore from '@tajawal/web-flights-sdk/lib/stores/fare-families.store';

// utils
import { buildTestId } from '@/utils/helpers.utils';
import { Observable } from '@/utils/observer/observer';
import { getFareFamilyCheckinContent, getFareFamilyPrice } from '@/utils/content.utils';
import { pushFareFamilyUpgradedEvent } from '@/utils/avoGtm.utils';
import formatSARToRiyalSymbol from '@/app-common/components/FormatSARToRiyalSymbol';
import { usePriceUpfrontAB } from '@/hooks/usePriceUpfrontAB';

// constants
import { OBSERVABLE_EVENTS } from '@/constants/common.constants';
import { CABIN_TYPES, OUTBOUND } from '@tajawal/web-flights-sdk/lib/constants';
import { AB_TESTING_FLAGS } from '@/constants/analytics.constants';

export type FareFamilyCardProps = {
  fareFamily: FareFamilyItinerary;
  index: number;
  itinerary: ItineraryModel;
  $fareFamilies: FareFamiliesStore['$fareFamilies'];
  onSelect: GenericCallback;
  isCreditShell: boolean;
  isDomestic: boolean;
  currentStep: FlightDirection;
  fareFamiliesSize: number;
  isPaxPage?: boolean;
  isSelected?: boolean;
  isFlightDetails?: boolean;
  handleCancelChangeClick?: GenericCallback;
  getItinerary: any;
};

function FareFamilyCard({
  fareFamily,
  index,
  itinerary,
  $fareFamilies,
  onSelect,
  isCreditShell,
  isDomestic,
  currentStep,
  fareFamiliesSize,
  isPaxPage = false,
  isFlightDetails = false,
  isSelected = false,
  handleCancelChangeClick,
  getItinerary,
}: FareFamilyCardProps): JSX.Element {
  const { t } = useTranslation();
  const code = fareFamily?.code;
  const {
    validatingCarrier,
    searchRequest: { isRoundTrip },
  } = itinerary || {};
  const brandedFares = $fareFamilies?.getBrandedFares(`${validatingCarrier}_${code}`);
  const testId = buildTestId('FareFamilyCard', `Option${index}`);
  const { hasFreeAirportCheckin, checkinTimeBeforDeparture, timeUnit, airportCheckinPrice, currency } =
    brandedFares?.checkinInfo || {};
  const isUnderSeatIncluded = brandedFares?.underSeatBaggage?.isIncluded;
  const hasOnlineCheckinInfo = checkinTimeBeforDeparture && timeUnit;
  const checkinContent = getFareFamilyCheckinContent(checkinTimeBeforDeparture, timeUnit, t);
  const { isPriceUpfrontEnabled } = usePriceUpfrontAB();
  const totalPrice = getFareFamilyPrice(fareFamily, isPriceUpfrontEnabled);

  const fareFamilyItinerary: ItineraryModel = getItinerary(fareFamily?.id);

  const searchQuery = itinerary?.searchRequest?.query;
  const splitAttributes = useComputeSplitQueryAttrs(searchQuery);
  const { cabinType } = useQuery(searchQuery);
  const isPremiumEconomyUpgrade = fareFamilyItinerary?.hasPremiumEconomyCabin && cabinType === CABIN_TYPES.ECONOMY;
  const { active: isPremiumEconomyHighlightEnabled } = useAB(
    AB_TESTING_FLAGS.PREMIUM_ECONOMY_HIGHLIGHT,
    splitAttributes
  );
  const isVariant = isPremiumEconomyUpgrade && isPremiumEconomyHighlightEnabled;

  function handleSelect() {
    onSelect();
    pushFareFamilyUpgradedEvent({
      price: +fareFamily?.displayPrice?.total,
      hasCheckinBaggage: brandedFares?.checkInBaggage?.isIncluded,
      cardPosition: index,
      isCancellationAvailable: brandedFares?.cancellation?.isAvailable,
      cancellationFee: brandedFares?.cancellation?.price,
      dateChangeAvailable: brandedFares?.flightChange?.isAvailable,
      dateChangeFee: brandedFares?.flightChange?.price,
      numberOfFareFamilies: fareFamiliesSize,
      itinerary: fareFamilyItinerary,
    });
    if ((isDomestic && currentStep === OUTBOUND) || isPaxPage) return;
    Observable.getInstance().notify(OBSERVABLE_EVENTS.FLIGHT_DETAILS, { isOpen: true });
  }

  function onCancelChangeClick(e) {
    e.stopPropagation();
    handleCancelChangeClick(0, fareFamilyItinerary);
  }

  return (
    <Styled.CardWrapper
      $isFlightDetails={isFlightDetails}
      $isHighlighted={isVariant}
      data-testid={testId}
      onClick={handleSelect}
    >
      <Styled.CardHeader $isFlightDetails={isFlightDetails} $isHighlighted={isVariant}>
        <Typography
          testId={buildTestId(testId, 'FareFamilyCode')}
          sx={{ flex: 1, textTransform: 'uppercase' }}
          variant={TYPOGRAPHY_VARIANT.HEADING4BLD}
          component="h4"
        >
          {code}
        </Typography>
        <Styled.Price>
          <Typography
            testId={buildTestId(testId, 'FareFamilyPriceTotal')}
            variant={TYPOGRAPHY_VARIANT.HEADING4BLD}
            component="h4"
          >
            <CurrencySymbol variant={TYPOGRAPHY_VARIANT.HEADING4BLD} currency={fareFamily?.displayPrice?.currency}>
              {totalPrice}
            </CurrencySymbol>
          </Typography>
          {isRoundTrip && !isPriceUpfrontEnabled && (
            <Typography
              sx={{ textAlign: 'end' }}
              color={COLOR_VARIANTS.GREYCHATEAU}
              variant={TYPOGRAPHY_VARIANT.CAPTIONREG}
              component="span"
            >
              {t('SearchResults.SearchCard_TotalRoundtripPrice')}
            </Typography>
          )}
        </Styled.Price>
      </Styled.CardHeader>
      <Styled.DividerWrapper $isHighlighted={isVariant}>
        <Styled.HalfCircle $isFlightDetails={isFlightDetails} />
        <Styled.Divider />
        <Styled.HalfCircle $rightSide $isFlightDetails={isFlightDetails} />
        <ClippingSVG isFlightDetails={isFlightDetails} isPremium={isVariant} />
      </Styled.DividerWrapper>
      <Styled.Content $isFlightDetails={isFlightDetails}>
        {isVariant && (
          <Styled.BenefitSection>
            <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
              {t('FareFamilyCard.UpgradeBenefitsTitle')}
            </Typography>
            <Styled.PremiumBenefitContent>
              <RuleItem
                icon={<Icons.Legroom width="24" height="24" color={color(COLOR_VARIANTS.FOREST100)} />}
                message={t('FareFamilyCard.SpaciousSeats')}
                testIdPrefix={buildTestId(testId, 'PremiumEconomyBenefits')}
              />
              <RuleItem
                icon={<Icons.KnifeAndFork width="24" height="24" color={color(COLOR_VARIANTS.FOREST100)} />}
                message={t('FareFamilyCard.PremiumMeals')}
                testIdPrefix={buildTestId(testId, 'PremiumEconomyBenefits')}
              />
              <RuleItem
                icon={<Icons.Wine width="24" height="24" color={color(COLOR_VARIANTS.FOREST100)} />}
                message={t('FareFamilyCard.Beverages')}
                testIdPrefix={buildTestId(testId, 'PremiumEconomyBenefits')}
              />{' '}
              <RuleItem
                icon={<Icons.TicketIcon width="24" height="24" color={color(COLOR_VARIANTS.FOREST100)} />}
                message={t('FareFamilyCard.PriorityBoarding')}
                testIdPrefix={buildTestId(testId, 'PremiumEconomyBenefits')}
              />
            </Styled.PremiumBenefitContent>
          </Styled.BenefitSection>
        )}
        <Styled.BenefitSection>
          <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
            {t('FareFamilyCard.Baggage_Allowance')}
          </Typography>
          <Styled.BenefitContent>
            {isUnderSeatIncluded && (
              <BaggageItem isUnderSeatBaggage testIdPrefix={testId} baggageInfo={brandedFares?.underSeatBaggage} />
            )}
            <BaggageItem testIdPrefix={testId} baggageInfo={brandedFares?.cabinBaggage} />
            <BaggageItem testIdPrefix={testId} isChecked baggageInfo={brandedFares?.checkInBaggage} />
          </Styled.BenefitContent>
        </Styled.BenefitSection>
        <Styled.BenefitSection>
          <Styled.CancelContainer>
            <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
              {t('FareFamilyCard.Cancel_Date_Change')}
            </Typography>
            {isFlightDetails && (
              <Icons.InfoCircleIcon
                onClick={(e) => onCancelChangeClick(e)}
                cursor="pointer"
                width="20"
                height="20"
                color={color(COLOR_VARIANTS.AQUA100)}
              />
            )}
          </Styled.CancelContainer>
          <Styled.BenefitContent>
            <FareRuleItem testIdPrefix={testId} rule={brandedFares?.cancellation} isCreditShell={isCreditShell} />
            <FareRuleItem
              testIdPrefix={testId}
              isChangeRule
              rule={brandedFares?.flightChange}
              isCreditShell={isCreditShell}
            />
          </Styled.BenefitContent>
        </Styled.BenefitSection>
        {hasOnlineCheckinInfo && (
          <Styled.BenefitSection>
            <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4">
              {t('FareFamilyCard.Checkin')}
            </Typography>
            <Styled.BenefitContent>
              <RuleItem
                message={formatSARToRiyalSymbol({
                  disclaimer: hasFreeAirportCheckin
                    ? t('FareFamilyCard.FreeCheckin')
                    : t('FareFamilyCard.PaidAirportCheckin', { price: airportCheckinPrice, currency }),
                })}
                icon={
                  hasFreeAirportCheckin ? (
                    <CircleIcons.Check width="24" height="24" />
                  ) : (
                    <CircleIcons.Fees height="24" width="24" />
                  )
                }
                testIdPrefix={buildTestId(testId, 'Checkin')}
              />
              <RuleItem
                message={formatSARToRiyalSymbol({ disclaimer: checkinContent })}
                icon={<CircleIcons.Info width="16px" height="16px" />}
                testIdPrefix={buildTestId(testId, 'CheckinInfo')}
              />
            </Styled.BenefitContent>
          </Styled.BenefitSection>
        )}
        <Styled.CTAWrapper>
          {isFlightDetails || isPaxPage ? (
            <Button
              size="small"
              testId={buildTestId(testId, 'SelectCTA')}
              appearance={isSelected ? 'ghost' : 'secondary'}
              fullWidth
            >
              <Styled.ButtonTitle>
                {isSelected ? (
                  <>
                    <Icons.Check width="16px" height="16px" />
                    {t('FareFamilyCard.Selected')}
                  </>
                ) : (
                  <>{isVariant ? t('FareFamilyCard.UpgradeCTA') : t('FareFamilyCard.Select')}</>
                )}
              </Styled.ButtonTitle>
            </Button>
          ) : (
            <Button size="small" testId={buildTestId(testId, 'SelectCTA')} appearance="primary" fullWidth>
              {isVariant ? (t('FareFamilyCard.UpgradeCTA') as string) : (t('FareFamilyCard.Select') as string)}
            </Button>
          )}
        </Styled.CTAWrapper>
      </Styled.Content>
    </Styled.CardWrapper>
  );
}

export default FareFamilyCard;
